import { getCurrentPermissions } from '@wix/ambassador-blog-v3-blog-http/http';
import { Scope, Status, type Permissions } from '@wix/ambassador-blog-v3-blog-http/types';
import { getMember, getMyMember } from '@wix/ambassador-members-v1-member-http/http';
import { Set } from '@wix/ambassador-members-v1-member-http/types';
import { createAction } from '@wix/communities-blog-client-common';
import type { FeedPageThunkAction } from '@app/external/feed-page/types';
import { createPromisifiedAction } from '../../actions-promisifier/create-promisified-action';
import { blogAppDefId } from '../../constants/apps';
import { initializeInstanceValues } from '../instance-values/instance-values-actions';
import { config } from './permissions-checker-config';
import { type CurrentUser } from './types';

export const fetchCurrentUserRequest = createAction('currentUser/FETCH_REQUEST');
export const fetchCurrentUserSuccess = createAction(
  'currentUser/FETCH_SUCCESS',
  (payload: CurrentUser | undefined) => payload,
);
export const fetchCurrentUserFailure = createAction('currentUser/FETCH_FAILURE');

const getRolesForPermissionsChecker = (permissions: Permissions | undefined) => {
  const UNKNOWN_SCOPE = config[Scope.UNKNOWN_SCOPE];
  if (!permissions || !permissions.scope) {
    return UNKNOWN_SCOPE;
  }

  return config[permissions.scope] ?? UNKNOWN_SCOPE;
};

const guestPermissions = (visitorId: string | undefined): Permissions => ({
  visitorId,
  isBlocked: false,
  privacyStatus: Status.PUBLIC,
  scope: Scope.GUEST,
});

export const fetchCurrentUser =
  (tokenToPreviewPost?: string): FeedPageThunkAction<Promise<void>> =>
  async (dispatch, getState, { httpClient, errorHandler, platformApi }) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    const { id: userId, loggedIn } = platformApi.user.currentUser;

    dispatch(fetchCurrentUserRequest());

    try {
      const instance = tokenToPreviewPost ?? platformApi.site.getAppToken?.(blogAppDefId);

      const signedInstance = tokenToPreviewPost
        ? { signedInstance: tokenToPreviewPost }
        : undefined;

      dispatch(initializeInstanceValues(instance));

      if (!tokenToPreviewPost && loggedIn === false && platformApi.window.viewMode === 'Site') {
        const permissions = guestPermissions(userId);
        dispatch(
          fetchCurrentUserSuccess({
            permissions,
            appStore: {
              [blogAppDefId]: {
                permissions: {
                  role: getRolesForPermissionsChecker(permissions),
                },
              },
            },
          }),
        );
        return;
      }

      const {
        data: { permissions },
      } = await withErrorHandler(
        () => httpClient.request(getCurrentPermissions({}), signedInstance),
        {
          errorCodesMap: {},
        },
      );

      const isVisitor = permissions?.visitorId != null;
      const appStore = {
        [blogAppDefId]: {
          permissions: {
            role: getRolesForPermissionsChecker(permissions),
          },
        },
      };

      if (isVisitor) {
        dispatch(
          fetchCurrentUserSuccess({
            permissions,
            appStore,
          }),
        );
        return;
      }

      const {
        data: { member },
      } = tokenToPreviewPost
        ? await withErrorHandler(
            () => httpClient.request(getMyMember({ fieldsets: [Set.EXTENDED] }), signedInstance),
            {
              errorCodesMap: {},
            },
          )
        : await withErrorHandler(
            () =>
              httpClient.request(
                getMember({ id: userId, fieldsets: [Set.EXTENDED] }),
                signedInstance,
              ),
            {
              errorCodesMap: {},
            },
          );

      const user = {
        ...member,
        permissions,
        appStore,
      };

      dispatch(fetchCurrentUserSuccess(user));
    } catch (error) {
      dispatch(fetchCurrentUserFailure());
      getResolvedError(error);
    }
  };

export const fetchCurrentUserPromisified = createPromisifiedAction(
  fetchCurrentUser,
  (result) => result,
);
